import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { formatDate } from '../../utils/formatDate';

export default function ViewNotificationCampaignDialog({
    open,
    onClose,
    campaign,
    onSend,
    onEdit,
    canEdit,
    onDelete,
    canDelete,
}) {
    const [sending, setSending] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    if (!campaign) return null;

    const ActionTypeLabel = ({ actionType }) => {
        const labels = {
            EVENT: 'Evento',
            CONTENT: 'Conteúdo',
            PRODUCT: 'Produto',
            URL: 'URL',
            NONE: 'Nenhuma',
        };
        return labels[actionType] || actionType;
    };

    const NotificationTypeLabel = ({ type }) => {
        const labels = {
            GENERAL: 'Geral',
            PROMOTION: 'Promoção',
            EVENT: 'Evento',
            ACHIEVEMENT: 'Conquista',
            REMINDER: 'Lembrete',
        };
        return labels[type] || type;
    };

    const handleSendClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmSend = async () => {
        setSending(true);
        try {
            await onSend(campaign.id);
            setShowConfirm(false);
        } catch (error) {
            console.error('Erro ao enviar:', error);
        } finally {
            setSending(false);
        }
    };

    const handleCancelSend = () => {
        setShowConfirm(false);
    };

    const isAlreadySent = !!campaign.sentAt;

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Detalhes da Campanha
                <IconButton
                    aria-label='close'
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {/* Status */}
                    <Box>
                        <Chip
                            label={isAlreadySent ? 'Enviada' : 'Pendente'}
                            color={isAlreadySent ? 'success' : 'warning'}
                            variant={isAlreadySent ? 'filled' : 'outlined'}
                        />
                    </Box>

                    {/* Título */}
                    <Box>
                        <Typography
                            variant='overline'
                            sx={{ color: '#999', fontSize: '11px' }}
                        >
                            Título
                        </Typography>
                        <Typography
                            variant='h6'
                            sx={{ color: '#292929', fontWeight: '500' }}
                        >
                            {campaign.title}
                        </Typography>
                    </Box>

                    {/* Mensagem */}
                    <Box>
                        <Typography
                            variant='overline'
                            sx={{ color: '#999', fontSize: '11px' }}
                        >
                            Mensagem
                        </Typography>
                        <Typography variant='body1' sx={{ color: '#555' }}>
                            {campaign.message}
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Tipo de Notificação */}
                    <Stack direction='row' spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant='overline'
                                sx={{ color: '#999', fontSize: '11px' }}
                            >
                                Tipo
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{ color: '#292929' }}
                            >
                                <NotificationTypeLabel type={campaign.type} />
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant='overline'
                                sx={{ color: '#999', fontSize: '11px' }}
                            >
                                Ação
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{ color: '#292929' }}
                            >
                                <ActionTypeLabel
                                    actionType={campaign.actionType}
                                />
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Evento Vinculado */}
                    {campaign.eventTitle && (
                        <Box>
                            <Typography
                                variant='overline'
                                sx={{ color: '#999', fontSize: '11px' }}
                            >
                                Evento Vinculado
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{ color: '#292929' }}
                            >
                                {campaign.eventTitle}
                            </Typography>
                        </Box>
                    )}

                    <Divider />

                    {/* Datas */}
                    <Stack direction='column' spacing={1}>
                        <Box>
                            <Typography
                                variant='overline'
                                sx={{ color: '#999', fontSize: '11px' }}
                            >
                                Criada em
                            </Typography>
                            <Typography variant='body2' sx={{ color: '#555' }}>
                                {formatDate(campaign.createdAt)}
                            </Typography>
                        </Box>

                        {campaign.sentAt && (
                            <Box>
                                <Typography
                                    variant='overline'
                                    sx={{ color: '#999', fontSize: '11px' }}
                                >
                                    Enviada em
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{ color: '#555' }}
                                >
                                    {formatDate(campaign.sentAt)}
                                </Typography>
                            </Box>
                        )}
                    </Stack>

                    {/* Confirmação de Envio */}
                    {showConfirm && !isAlreadySent && (
                        <Alert severity='warning' sx={{ mt: 2 }}>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Atenção!</strong> Esta ação enviará a
                                notificação push para todos os jogadores. <br />
                                <strong>
                                    Uma vez enviado, não será possível editar ou
                                    excluir a campanha
                                </strong>
                                .<br />
                                Deseja continuar?
                            </Typography>
                            <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                                <Button
                                    size='small'
                                    variant='contained'
                                    color='warning'
                                    onClick={handleConfirmSend}
                                    disabled={sending}
                                    endIcon={
                                        sending ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <SendIcon />
                                        )
                                    }
                                >
                                    {sending ? 'Enviando...' : 'Sim, enviar'}
                                </Button>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    onClick={handleCancelSend}
                                    disabled={sending}
                                >
                                    Cancelar
                                </Button>
                            </Stack>
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined'>
                    Fechar
                </Button>

                {canDelete && !isAlreadySent && (
                    <Button
                        onClick={() => onDelete?.(campaign)}
                        variant='outlined'
                        color='error'
                    >
                        Excluir
                    </Button>
                )}

                {canEdit && !isAlreadySent && (
                    <Button
                        onClick={() => onEdit?.(campaign)}
                        variant='outlined'
                    >
                        Editar
                    </Button>
                )}

                {!isAlreadySent && !showConfirm && (
                    <Button
                        onClick={handleSendClick}
                        variant='contained'
                        color='primary'
                        startIcon={<SendIcon />}
                    >
                        Enviar Campanha
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
